import { createSelector } from 'reselect'
import { ExternalWalletStatus, isFiat, isCrypto, CurrenciesCrypto, BankAccountStatus } from '@sparling/financial-common'
import { RootState } from '../types'
import { instrumentsAllSelector } from './instruments'

export const isUserLoggingInSelector = createSelector(
  (state: RootState) => state.user.loggingIn,
  (state): boolean => state === 'LOADING',
)

export const userDataSelector = (state: RootState): RootState['user']['data'] => state.user.data

export const userBaseCurrencySelector = createSelector(userDataSelector, (data) => {
  return data?.currency || 'USD'
})

export const getUserCommissionFee = createSelector(
  (state: RootState) => state.user.data,
  (data) => data?.commissionFee,
)

export const userBankAccountsSelector = (state: RootState): RootState['user']['bankAccounts'] => state.user.bankAccounts
export const userSfBankAccountsSelector = (state: RootState): RootState['user']['sfBankAccounts'] =>
  state.user.sfBankAccounts

export const userWalletsSelector = (state: RootState): RootState['user']['wallets'] => state.user.wallets
export const userAvailableWalletCurrenciesSelector = createSelector(
  userWalletsSelector,
  instrumentsAllSelector,
  (wallets, instruments): Array<string> => {
    const excludedCurrencies: string[] =
      wallets.data.items
        ?.filter((wallet) => ![ExternalWalletStatus.ARCHIVED, ExternalWalletStatus.REJECTED].includes(wallet.status))
        ?.map((wallet) => String(wallet.currency).toUpperCase()) || []

    const cryptoValues = Object.values(CurrenciesCrypto).map((v) => String(v).toUpperCase())

    const supportedCryptoCurrencies = Array.from(
      new Set(
        (instruments || [])
          .flatMap((inst) => {
            const u = inst.underlier || ''
            if (typeof u !== 'string' || u.length < 3) return []

            const U = u.toUpperCase()
            const out: string[] = []

            // Case 1: <CRYPTO><FIAT-3>
            const suffix3 = U.slice(-3)
            if (isFiat(suffix3)) {
              const base = U.slice(0, U.length - 3)
              if (isCrypto(base)) out.push(base)
              return out
            }

            // Case 2: Try detect base by prefix <CRYPTO><QUOTE>
            // Even if QUOTE is unknown, we still include BASE if it's a valid crypto.
            // eslint-disable-next-line no-restricted-syntax
            for (const baseCode of cryptoValues) {
              // eslint-disable-next-line
              if (!baseCode) continue
              if (U.startsWith(baseCode)) {
                const quote = U.slice(baseCode.length)
                if (isCrypto(baseCode)) out.push(baseCode)
                if (quote) {
                  if (isCrypto(quote)) out.push(quote)
                  // if quote is fiat or unknown, we still keep base only
                }
                return out
              }
            }

            // Case 3: Fallback — detect quote by suffix <BASE><CRYPTO>
            // eslint-disable-next-line no-restricted-syntax
            for (const c of cryptoValues) {
              // eslint-disable-next-line
              if (!c) continue
              if (U.endsWith(c)) {
                const base = U.slice(0, U.length - c.length)
                if (isCrypto(base)) out.push(base)
                if (isCrypto(c)) out.push(c)
                return out
              }
            }

            return out
          })
          .filter((c): c is string => !!c),
      ),
    )

    return supportedCryptoCurrencies.filter((currency) => !excludedCurrencies.includes(currency))
  },
)

export const userSfWalletsSelector = (state: RootState): RootState['user']['sfWallets'] => state.user.sfWallets

export const userWalletsLoadingStateSelector = (state: RootState): RootState['user']['userWalletsList']['status'] =>
  state.user.userWalletsList.status

export const isLoadingUserWalletsListSelector = createSelector(
  userWalletsLoadingStateSelector,
  (status) => status === 'LOADING',
)
export const userWalletsListSelector = (state: RootState): RootState['user']['userWalletsList']['data'] =>
  state.user.userWalletsList.data

export const userActiveWalletsSelector = createSelector(
  userWalletsListSelector,
  (wallets) => wallets?.filter((w) => w?.status === ExternalWalletStatus.ACTIVE) ?? [],
)

export const userWalletsListEnumSelector = createSelector(
  userActiveWalletsSelector,
  (wallets): Array<{ value: string; label: string }> =>
    wallets.map((wallet) => ({ value: wallet.currency, label: wallet.currency })) || [],
)

export const getClientTypesForAssignToUserLoadingState = (
  state: RootState,
): RootState['user']['clientTypesForAssignToUser']['status'] => state.user.clientTypesForAssignToUser.status

export const clientTypesForAssignToUserLoading = createSelector(
  getClientTypesForAssignToUserLoadingState,
  (state): boolean => state === 'LOADING',
)

export const getClientTypesForAssignToUserData = (
  state: RootState,
): RootState['user']['clientTypesForAssignToUser']['data'] => state.user.clientTypesForAssignToUser.data

export const getClientTypesForAssignToUserEnum = createSelector(getClientTypesForAssignToUserData, (clientTypes) =>
  clientTypes.items.map((clientType) => ({ value: clientType.id, label: clientType.name })),
)

export const userBankAccountListSelector = (state: RootState): RootState['user']['userBankAccountList']['data'] => {
  return state.user.userBankAccountList.data
}
export const userActiveBankAccountsSelector = createSelector(
  userBankAccountListSelector,
  (bankAccounts) => bankAccounts?.filter((ba) => ba?.status === BankAccountStatus.APPROVED) ?? [],
)
